import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errors';
import { StaffRole, UserRole } from '@/types/enums';
import { useQuery, useMutation, UseMutateFunction, UseMutationResult } from '@tanstack/react-query';
import {
  getUserRoleQueryKey,
  getStaffProfileQueryKey,
  getTutorProfileQueryKey,
  getClientProfileQueryKey,
} from '@/utils/query-keys';
import { z } from 'zod';

type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];
export type User = Awaited<ReturnType<typeof supabase.auth.getUser>>['data']['user'] & {
  access_token?: string;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  userRoles: UserRole[];
  userRolesLoading: boolean;
  staffProfile: z.output<typeof StaffProfileSchema> | null | undefined;
  staffProfileLoading: boolean;
  tutorProfile: z.output<typeof TutorProfileSchema> | null | undefined;
  tutorProfileLoading: boolean;
  clientProfile: z.output<typeof ClientProfileSchema> | null | undefined;
  clientProfileLoading: boolean;
  signIn: UseMutationResult<void, unknown, { email: string; password: string }>;
  signUp: UseMutationResult<
    void,
    unknown,
    { email: string; password: string; firstName: string; lastName: string; role: string },
    unknown
  >;
  signInWithMagicLink: UseMutationResult<void, unknown, { email: string }>;
  signInWithAzure: UseMutationResult<void, unknown, void>;
  signOut: UseMutationResult<void, unknown, void>;
  sessionLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);

  const user = useMemo(
    () =>
      session?.user
        ? {
            ...session.user,
            access_token: session.access_token,
          }
        : null,
    [session],
  );

  // User role
  const {
    data: userRoles = [],
    error: userRolesError,
    isLoading: userRolesLoading,
  } = useQuery({
    queryKey: getUserRoleQueryKey(user?.id),
    queryFn: () => fetchUserRoles(user?.id),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (userRolesError) {
      console.error('Error fetching user role:', userRolesError);
    }
  }, [userRolesError]);

  // Staff profile
  const {
    data: staffProfile,
    error: staffProfileError,
    isLoading: staffProfileLoading,
  } = useQuery({
    queryKey: getStaffProfileQueryKey(user?.id),
    queryFn: () => (user?.id ? fetchStaffProfile(user.id) : null),
    enabled: userRoles.includes('staff'),
  });

  useEffect(() => {
    if (staffProfileError) {
      console.error('Error fetching staff profile:', staffProfileError);
    }
  }, [staffProfileError]);

  // Tutor profile
  const {
    data: tutorProfile,
    error: tutorProfileError,
    isLoading: tutorProfileLoading,
  } = useQuery({
    queryKey: getTutorProfileQueryKey(user?.id),
    queryFn: () => (user?.id ? fetchTutorProfile(user.id) : null),
    enabled: userRoles.includes('tutor'),
  });

  useEffect(() => {
    if (tutorProfileError) {
      console.error('Error fetching tutor profile:', tutorProfileError);
    }
  }, [tutorProfileError]);

  // Client profile
  const {
    data: clientProfile,
    error: clientProfileError,
    isLoading: clientProfileLoading,
  } = useQuery({
    queryKey: getClientProfileQueryKey(user?.id),
    queryFn: () => (user?.id ? fetchClientProfile(user.id) : null),
    enabled: userRoles.includes('client'),
  });

  useEffect(() => {
    if (clientProfileError) {
      console.error('Error fetching client profile:', clientProfileError);
    }
  }, [clientProfileError]);

  const setupSession = useMutation({
    mutationFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      setSession(session);
      return null;
    },
    onError: (error: unknown) => {
      console.error('Error setting up session:', error);
    },
  });

  const hasSession = !!session;
  useEffect(() => {
    if (!hasSession) {
      setupSession.mutate();
    }

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip the initial SIGNED_IN event as it's handled by setupSession
      if (event === 'SIGNED_IN' && !session) {
        return;
      }

      setSession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [hasSession, setupSession.mutate]);

  useEffect(() => {
    // Set up a timer to refresh auth session every 10 minutes
    const refreshTimer = setInterval(
      async () => {
        try {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error('Error refreshing session:', error);
          }
        } catch (error) {
          console.error('Error refreshing session:', error);
        }
      },
      10 * 60 * 1000,
    ); // 10 minutes

    return () => {
      clearInterval(refreshTimer);
    };
  }, []);

  // Sign in mutation
  const signIn = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        toast.error(error.message);
        throw error;
      }
      toast.success('Signed in successfully!');
      const redirectTo = new URLSearchParams(window.location.search).get('redirect_to');
      const decodedRedirect = decodeURIComponent(redirectTo || '/');
      window.location.href = validateRedirectUrl(decodedRedirect);
    },
    onError: (error: unknown) => {
      toast.error(`Error signing in: ${getErrorMessage(error)}`);
      console.error('Error signing in:', getErrorMessage(error));
    },
  });

  // Sign up mutation
  const signUp = useMutation({
    mutationFn: async ({
      email,
      password,
      firstName,
      lastName,
      role,
    }: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      role: string;
    }) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            user_role: role,
          },
        },
      });

      if (error) {
        throw error;
      }

      toast.success('Signed up successfully! Please check your email for confirmation.');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      console.error('Error signing up:', getErrorMessage(error));
    },
  });

  // Magic link mutation
  const signInWithMagicLink = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        throw error;
      }

      toast.success('Magic link sent! Please check your email.');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      console.error('Error sending magic link:', getErrorMessage(error));
    },
  });

  // Azure OAuth mutation
  const signInWithAzure = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'email',
        },
      });

      if (error) {
        throw error;
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      console.error('Error signing in with Azure:', getErrorMessage(error));
    },
  });

  // Sign out mutation
  const signOut = useMutation({
    mutationFn: async () => {
      console.log('Attempting to sign out...');

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        toast.error(error.message);

        await supabase.auth.refreshSession();
        throw error;
      }
      console.log('Sign out successful');
      toast.success('Signed out successfully!');

      // Clear the state first to prevent any race conditions
      setSession(null);

      // Navigate after successful sign out
      window.location.href = '/auth';
    },
    onError: (error: unknown) => {
      toast.error(`Error signing out:${getErrorMessage(error)}`);
      console.error('Error signing out:', getErrorMessage(error));
    },
  });

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        userRoles,
        userRolesLoading,
        staffProfile,
        staffProfileLoading,
        tutorProfile,
        tutorProfileLoading,
        clientProfile,
        clientProfileLoading,
        signIn,
        signUp,
        signInWithMagicLink,
        signInWithAzure,
        signOut,
        sessionLoading: setupSession.isPending || setupSession.isIdle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const validateRedirectUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url, location.origin);
    if (parsedUrl.origin === location.origin) {
      return parsedUrl.pathname + parsedUrl.search;
    }
  } catch {}
  return '/';
};

const fetchUserRoles = async (userId: string | null | undefined) => {
  if (!userId) return [];

  const { data, error } = await supabase.from('user_roles').select('role').eq('user_id', userId);

  if (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }

  return z
    .array(z.object({ role: UserRole }))
    .parse(data)
    .map((row) => row.role);
};

const StaffProfileSchema = z.object({
  name: z.string(),
  access: StaffRole.nullable(),
});

const fetchStaffProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('staff_profiles')
    .select('name, access')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching staff profile:', error);
    return null;
  }

  return StaffProfileSchema.nullable().parse(data);
};

const TutorProfileSchema = z.object({
  id: z.number(),
  name: z.string(),
  tutor_type: z.union([z.literal('self_employed'), z.literal('professional')]),
  bio: z.string().nullable(),
});

const fetchTutorProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('tutor_profiles')
    .select('id, name, tutor_type, bio')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching tutor profile:', error);
    return null;
  }

  return TutorProfileSchema.nullable().parse(data);
};

const ClientProfileSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const fetchClientProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('contacts')
    .select('id, name')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching client profile:', error);
    return null;
  }

  return ClientProfileSchema.nullable().parse(data);
};
